import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { HabitType } from '../../../types';
import { formatTime12h } from '../../../utils/dateUtils';
import CellTypeSelect from '../CellTypeSelect';
import TimeScrollPicker from '../TimeScrollPicker';

export const HABIT_COLORS = [
  '#C25E45',
  '#6A866A',
  '#407D3F',
  '#7B4E71',
  '#C34B82',
  '#376D78',
];

export const HABIT_NAME_MAX = 50;
export const TASK_TITLE_MAX = 50;

export function RemainingCount({ length, max }: { length: number; max: number }) {
  const left = max - length;
  if (left > 10) return null;
  return (
    <span className={`field-remaining${left === 0 ? ' field-remaining--zero' : ''}`}>
      {left}
    </span>
  );
}

interface HabitFormFieldsProps {
  name: string;
  onNameChange: (value: string) => void;
  color: string;
  onColorChange: (value: string) => void;
  type: HabitType;
  onTypeChange: (value: HabitType) => void;
  scheduledTime: string;
  onScheduledTimeChange: (value: string) => void;
  onTimePickerOpenChange?: (open: boolean) => void;
  autoFocusName?: boolean;
  namePlaceholder?: string;
  onNameKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  children?: React.ReactNode;
}

const HabitFormFields: React.FC<HabitFormFieldsProps> = ({
  name,
  onNameChange,
  color,
  onColorChange,
  type,
  onTypeChange,
  scheduledTime,
  onScheduledTimeChange,
  onTimePickerOpenChange,
  autoFocusName,
  namePlaceholder,
  onNameKeyDown,
  children,
}) => {
  const { formatMessage: t } = useIntl();
  const [timePickerOpen, setTimePickerOpen] = useState(false);
  const timeSectionRef = useRef<HTMLDivElement>(null);
  const customColor = !HABIT_COLORS.includes(color);
  const setPickerOpen = (open: boolean) => {
    setTimePickerOpen(open);
    onTimePickerOpenChange?.(open);
  };

  useEffect(() => {
    if (!timePickerOpen) return;
    timeSectionRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [timePickerOpen]);

  return (
    <>
      <div className="col-edit-section">
        <span className="col-edit-label">{t({ id: 'monthly.popover.name' })}</span>
        <div className="field-with-count">
          <input
            className="col-edit-input"
            value={name}
            autoFocus={autoFocusName}
            placeholder={namePlaceholder}
            maxLength={HABIT_NAME_MAX}
            onChange={(e) => onNameChange(e.target.value.slice(0, HABIT_NAME_MAX))}
            onKeyDown={onNameKeyDown}
          />
          <RemainingCount length={name.length} max={HABIT_NAME_MAX} />
        </div>
      </div>

      <div className="col-edit-section">
        <span className="col-edit-label">{t({ id: 'monthly.popover.colour' })}</span>
        <div className="add-habit-colors">
          {HABIT_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              className={`color-swatch${color === c ? ' color-swatch--active' : ''}`}
              style={{ '--swatch-color': c } as React.CSSProperties}
              onClick={() => onColorChange(c)}
            />
          ))}
          <label
            className={`color-swatch color-swatch--wheel${customColor ? ' color-swatch--active' : ''}`}
            style={{ '--swatch-color': customColor ? color : undefined } as React.CSSProperties}
            title={t({ id: 'monthly.popover.customColour' })}
            aria-label={t({ id: 'monthly.popover.customColour' })}
          >
            <input
              type="color"
              className="color-wheel-input"
              value={color}
              onChange={(e) => onColorChange(e.target.value)}
            />
          </label>
        </div>
      </div>

      <div className="col-edit-section" ref={timeSectionRef}>
        <span className="col-edit-label">{t({ id: 'monthly.popover.calTime' })}</span>
        <div className="col-edit-time">
          <div className="col-edit-time-row">
            <button
              type="button"
              className={`col-edit-input col-edit-input--time-btn${scheduledTime ? ' col-edit-input--time-btn--has-value' : ''}`}
              onClick={() => setPickerOpen(!timePickerOpen)}
            >
              {scheduledTime ? formatTime12h(scheduledTime) : '--:-- --'}
            </button>
            {scheduledTime && (
              <button
                type="button"
                className="col-edit-time-clear"
                title={t({ id: 'monthly.popover.clearTime' })}
                onClick={() => {
                  onScheduledTimeChange('');
                  setPickerOpen(false);
                }}
              >
                ✕
              </button>
            )}
          </div>
          {timePickerOpen && (
            <TimeScrollPicker
              value={scheduledTime}
              onChange={onScheduledTimeChange}
              onClose={() => setPickerOpen(false)}
            />
          )}
        </div>
      </div>

      <div className="col-edit-section">
        <span className="col-edit-label">{t({ id: 'monthly.popover.type' })}</span>
        <CellTypeSelect value={type} onChange={onTypeChange} />
      </div>

      {children}
    </>
  );
};

export default HabitFormFields;
